import React, { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Manager, Popper, Reference } from 'react-popper';

import { isMobile as isMobileSelector } from 'redux/modules/device-type';
import { useOutsideClick } from 'utils/hooks/use-outside-click';
import { useSummaryRedirect } from 'utils/hooks/summary-redirect';
import { useFetchFavorites } from 'widgets/favorites/hook/use-fetch-favorites';
import { FAV_AND_RECENT_TYPE_SCOUT_PLAYER, FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import { SearchField } from '../search-components/search-field';
import { SearchResult } from '../search-result';
import { useSearchInputField } from '../../hooks/use-search-input-field';
import { useFavoriteToggle } from './hooks/use-favorite-toggle';
import { useFetchLastTeamGame } from './hooks/fetch-last-team-game';
import { useFetchPlayersProfile } from './hooks/fetch-players-profile';
import { useSearchPlayerTeams } from './hooks/use-search-player-teams';
import { useSearchShortcuts } from './hooks/use-search-shortcuts';
import styles from './top-search-panel.module.css';

type TopSearchPanelProps = {
    /** a team opens its last game in the summary instead of the team page */
    isSummaryMode?: boolean;
    isFavoriteListNeedToFetch: boolean;
};

/** The search of the header: the field and its dropdown with the results and the favorites */
const TopSearchPanel = ({ isSummaryMode = false, isFavoriteListNeedToFetch }: TopSearchPanelProps) => {
    const isMobile = useSelector(isMobileSelector);
    const { inputValue, tooltipVisible, handleReset, handleInputChange, handleInputClick } = useSearchInputField();
    const search = useSearchPlayerTeams({ inputValue });

    // After a redirect both the search and the last game of the team are reset; the hook gives its reset only after
    // it is called with onRedirect, so the latest one is read through a ref
    const resetLastTeamGameRef = useRef<() => void>(() => undefined);
    const handleRedirect = useCallback(() => {
        resetLastTeamGameRef.current();
        handleReset();
    }, [handleReset]);
    const lastTeamGame = useFetchLastTeamGame({ isSummaryMode, onRedirect: handleRedirect });
    resetLastTeamGameRef.current = lastTeamGame.handleReset;
    useSummaryRedirect({ ...lastTeamGame.redirectParams, onRedirect: handleRedirect, isDisabled: !isSummaryMode });

    const fieldRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // The dropdown is in a portal: a press in the field or in the dropdown is inside
    useOutsideClick({ ref: [fieldRef, dropdownRef], callback: handleReset, isEnabled: tooltipVisible });
    useSearchShortcuts({ isOpen: tooltipVisible, onOpen: handleInputClick, onClose: handleReset });

    const { data: playerData, processing: favPlayerProcessing } = useFetchFavorites({
        initialCondition: isFavoriteListNeedToFetch,
        favType: FAV_AND_RECENT_TYPE_SCOUT_PLAYER
    });
    const { data: teamData, processing: favTeamProcessing } = useFetchFavorites({
        initialCondition: isFavoriteListNeedToFetch,
        favType: FAV_AND_RECENT_TYPE_TEAM,
        isAllTeams: true
    });
    const { handleFavoriteToggle } = useFavoriteToggle({ playerFavItems: playerData.favItems });
    const { allFavoritePlayers, isProcessing: profilesProcessing } = useFetchPlayersProfile(playerData);

    const processing =
        search.processing || lastTeamGame.processing || favTeamProcessing || favPlayerProcessing || profilesProcessing;
    const isFavoritesLoaded = !favTeamProcessing && !favPlayerProcessing;

    return (
        <Manager>
            <Reference>
                {({ ref }) => (
                    <div
                        className={styles.field}
                        ref={node => {
                            if (typeof ref === 'function') {
                                ref(node);
                            }
                            fieldRef.current = node;
                        }}
                    >
                        <SearchField
                            isMobile={isMobile}
                            isActive={tooltipVisible}
                            value={inputValue}
                            onClick={handleInputClick}
                            onChange={handleInputChange}
                            onCancel={handleReset}
                            processing={processing}
                        />
                    </div>
                )}
            </Reference>
            {createPortal(
                <Popper placement="bottom-start">
                    {({ placement, ref, style }) => (
                        // eslint-disable-next-line tpe/no-inline-style -- the position computed by popper
                        <div className={styles.popper} style={style} ref={ref} data-placement={placement}>
                            {tooltipVisible && isFavoritesLoaded ? (
                                <div className={styles.dropdown} ref={dropdownRef}>
                                    <SearchResult
                                        isFavorite
                                        players={search.players}
                                        teams={search.teams}
                                        onTeamSelect={lastTeamGame.handleTeamSelect}
                                        favTeamList={teamData.favItems ?? undefined}
                                        favPlayerList={playerData.favItems ?? undefined}
                                        allFavoritePlayers={allFavoritePlayers}
                                        allTeams={teamData.allTeams ?? undefined}
                                        onFavoriteToggle={handleFavoriteToggle}
                                        processing={processing}
                                        searchQuery={inputValue}
                                        searchFailed={search.failed}
                                    />
                                </div>
                            ) : null}
                        </div>
                    )}
                </Popper>,
                document.body
            )}
        </Manager>
    );
};

export default TopSearchPanel;
